import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSSE } from '../../hooks/useSSE'
import { Trash2, Plus, MoreHorizontal } from 'lucide-react'
import {
  ConfirmDialog,
  Menu,
  Drawer,
  Avatar,
  Chip,
  Button,
  MarkdownComposer,
  InlineText,
  InlineSelect,
  InlineDate,
  statusTone,
  priorityTone,
  cn,
} from '../../design'
import {
  getStory,
  updateStory,
  deleteStory,
  addSubtask,
  updateSubtask,
  deleteSubtask,
  listComments,
  addComment,
  deleteComment,
  listWorkflowStates,
  type StoryDetail,
  type StoryPriority,
} from '../../api/stories'
import { lookupUsers, lookupEpics, lookupSprints } from '../../api/users'
import { AttachmentsSection, useAttachments } from './Attachments'
import { renderMarkdown } from '../../lib/markdown'
import { uploadAttachment, attachmentsKey, isImage, type Attachment } from '../../api/attachments'
import { TimeLog } from './TimeLog'
import { useAuth } from '../../auth/AuthContext'
import { useNow } from '../../hooks/useNow'

export function StoryDrawer({
  storyKey,
  projectId,
  open,
  onOpenChange,
}: {
  storyKey: string | null
  projectId: number
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const qc = useQueryClient()

  const storyQ = useQuery({
    queryKey: ['story', storyKey],
    queryFn: () => getStory(storyKey!),
    enabled: !!storyKey && open,
    refetchOnWindowFocus: true,
  })

  const wfQ = useQuery({
    queryKey: ['workflow-states', projectId],
    queryFn: () => listWorkflowStates(projectId),
    enabled: open,
    staleTime: 60_000,
  })
  const usersQ = useQuery({
    queryKey: ['lookups', 'users'],
    queryFn: lookupUsers,
    enabled: open,
    staleTime: 60_000,
  })
  const epicsQ = useQuery({
    queryKey: ['lookups', 'epics', projectId],
    queryFn: () => lookupEpics(projectId),
    enabled: open,
    staleTime: 60_000,
  })
  const sprintsQ = useQuery({
    queryKey: ['lookups', 'sprints', projectId],
    queryFn: () => lookupSprints(projectId),
    enabled: open,
    staleTime: 60_000,
  })

  const story = storyQ.data
  const { user } = useAuth()
  // Mirrors the server gate (requireProjectEditor): viewers cannot delete.
  const canDelete = !!user && user.role !== 'viewer'
  const [confirmDelete, setConfirmDelete] = useState(false)
  // useSSE binds onEvent once per connection, so read the current story and
  // close handler through refs rather than the closure.
  const storyIdRef = useRef<number | null>(null)
  const closeRef = useRef(onOpenChange)
  useEffect(() => {
    storyIdRef.current = story?.id ?? null
    closeRef.current = onOpenChange
  })

  // Live updates while the drawer is open
  useSSE({
    projectId,
    enabled: open && !!storyKey,
    invalidate: {
      'story.updated':        [['story', storyKey], ['stories']],
      'story.status_changed': [['story', storyKey], ['stories']],
      'comment.added':        [['comments']],
      'dependency.added':     [['story', storyKey]],
      'subtask.deleted':      [['story', storyKey]],
    },
    // Someone else deleted the story we're looking at: nothing left to show.
    onEvent: (ev) => {
      if (ev.kind === 'story.deleted' && storyIdRef.current !== null && ev.payload.story_id === storyIdRef.current) {
        qc.invalidateQueries({ queryKey: ['stories'] })
        closeRef.current(false)
      }
    },
  })

  const deleteMut = useMutation({
    mutationFn: () => deleteStory(story!.id),
    onSuccess: () => {
      onOpenChange(false)
      qc.removeQueries({ queryKey: ['story', storyKey] })
      for (const key of [['stories'], ['dashboard'], ['agenda'], ['me']]) {
        qc.invalidateQueries({ queryKey: key })
      }
    },
  })

  const updateMut = useMutation({
    mutationFn: (patch: Parameters<typeof updateStory>[1]) => updateStory(storyKey!, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['story', storyKey] })
      qc.invalidateQueries({ queryKey: ['stories'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })

  function patch(p: Parameters<typeof updateStory>[1]) { updateMut.mutate(p) }

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      width="xl"
      title={
        story ? (
          <span className="flex items-center gap-2">
            <span className="font-mono text-xs text-fg-subtle">{story.key}</span>
            <span>·</span>
            <span className="text-sm font-medium">{story.type}</span>
            {canDelete && (
              <Menu
                trigger={
                  <button
                    className="ml-1 p-1 rounded text-fg-subtle hover:text-fg hover:bg-bg-muted"
                    aria-label="Story actions"
                  >
                    <MoreHorizontal size={14} />
                  </button>
                }
                items={[
                  {
                    kind: 'item',
                    label: 'Delete story…',
                    danger: true,
                    disabled: deleteMut.isPending,
                    onSelect: () => setConfirmDelete(true),
                  },
                ]}
              />
            )}
          </span>
        ) : (
          <span className="text-fg-subtle">Loading…</span>
        )
      }
      description={story?.epic_name ?? undefined}
    >
      {!story && storyQ.isLoading && (
        <div className="py-12 text-center text-fg-subtle">Loading story…</div>
      )}
      {!story && storyQ.error && (
        <div className="py-12 text-center text-danger">Failed to load story.</div>
      )}
      {deleteMut.error && (
        <div className="mb-3 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">
          Couldn't delete: {(deleteMut.error as Error).message}
        </div>
      )}
      {story && (
        <ConfirmDialog
          open={confirmDelete}
          onOpenChange={setConfirmDelete}
          title={`Delete ${story.key}?`}
          description={`"${story.title}" and its subtasks, comments and attachments will be permanently deleted. This cannot be undone.`}
          confirmLabel="Delete story"
          onConfirm={() => deleteMut.mutate()}
        />
      )}
      {story && (
        <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-6">
          {/* LEFT: title + description + subtasks + activity */}
          <div className="min-w-0">
            {/* Header chips */}
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <Chip size="sm" tone={story.type === 'bug' ? 'danger' : 'neutral'}>{story.type}</Chip>
              <Chip size="sm" tone={priorityTone(story.priority)}>{story.priority}</Chip>
              {story.epic_name && (
                <Chip size="sm" tone="epic" color={story.epic_color ?? '#9aa0a6'} dot>
                  {story.epic_name}
                </Chip>
              )}
              <span className="ml-auto text-[10px] font-mono text-fg-subtle">
                edited {timeAgo(story.updated_at)}
              </span>
            </div>

            {/* Title (inline edit) */}
            <div className="text-2xl font-semibold tracking-tight mb-4">
              <InlineText
                value={story.title}
                onCommit={v => patch({ title: v })}
              />
            </div>

            {/* Description (inline edit) */}
            <Section title="Description">
              <div className="text-sm text-fg-muted leading-relaxed whitespace-pre-wrap">
                <InlineText
                  value={story.description ?? ''}
                  onCommit={v => patch({ description: v || null })}
                  multiline
                  placeholder="Add a description…"
                  className="min-h-[24px]"
                />
              </div>
            </Section>

            {/* Subtasks */}
            <SubtasksSection story={story} />

            {/* dkbooks time tracking */}
            <TimeLog storyId={story.id} storyKey={story.key} />

            {/* Activity & comments */}
            <AttachmentsSection storyId={story.id} />
        <CommentsSection storyId={story.id} />
          </div>

          {/* RIGHT: metadata rail */}
          <aside className="rounded-xl border border-border bg-bg-muted/30 p-4 space-y-1 self-start">
            <MetaRow label="Status">
              <InlineSelect
                value={story.status_id}
                options={(wfQ.data ?? []).map(s => ({
                  value: s.id,
                  label: <Chip size="sm" tone={statusTone(s.category)}>{s.name}</Chip>,
                }))}
                display={
                  <Chip size="sm" tone={statusTone(story.status_category)}>
                    {story.status_name}
                  </Chip>
                }
                onCommit={v => patch({ status_id: v as number })}
              />
            </MetaRow>

            <MetaRow label="Assignee">
              <InlineSelect<number | null>
                value={story.assignee_id}
                options={[
                  { value: null, label: <span className="text-fg-subtle">Unassigned</span> },
                  ...(usersQ.data ?? []).map(u => ({
                    value: u.id,
                    label: (
                      <span className="flex items-center gap-2">
                        <Avatar size="xs" name={u.name} color={u.avatar_color ?? '#9aa0a6'} />
                        {u.name}
                      </span>
                    ),
                  })),
                ]}
                display={
                  story.assignee_name ? (
                    <span className="flex items-center gap-2 text-sm">
                      <Avatar size="xs" name={story.assignee_name} color={story.assignee_avatar_color ?? '#9aa0a6'} />
                      {story.assignee_name}
                    </span>
                  ) : (
                    <span className="text-sm text-fg-subtle">Unassigned</span>
                  )
                }
                onCommit={v => patch({ assignee_id: v })}
              />
            </MetaRow>

            <MetaRow label="Reporter">
              <span className="text-sm text-fg-muted">
                {story.reporter_id ? `User #${story.reporter_id}` : '—'}
              </span>
            </MetaRow>

            <MetaRow label="Epic">
              <InlineSelect<number | null>
                value={story.epic_id}
                options={[
                  { value: null, label: <span className="text-fg-subtle">No epic</span> },
                  ...(epicsQ.data ?? []).map(e => ({
                    value: e.id,
                    label: (
                      <Chip size="sm" tone="epic" color={e.color ?? '#9aa0a6'} dot>
                        {e.name}
                      </Chip>
                    ),
                  })),
                ]}
                display={
                  story.epic_name ? (
                    <Chip size="sm" tone="epic" color={story.epic_color ?? '#9aa0a6'} dot>
                      {story.epic_name}
                    </Chip>
                  ) : (
                    <span className="text-sm text-fg-subtle">—</span>
                  )
                }
                onCommit={v => patch({ epic_id: v })}
              />
            </MetaRow>

            <MetaRow label="Sprint">
              <InlineSelect<number | null>
                value={story.sprint_id}
                options={[
                  { value: null, label: <span className="text-fg-subtle">Backlog</span> },
                  ...(sprintsQ.data ?? []).map(sp => ({
                    value: sp.id,
                    label: <span>{sp.name}</span>,
                  })),
                ]}
                display={
                  <span className="text-sm">
                    {story.sprint_name ?? <span className="text-fg-subtle">Backlog</span>}
                  </span>
                }
                onCommit={v => patch({ sprint_id: v })}
              />
            </MetaRow>

            <MetaRow label="Priority">
              <InlineSelect<StoryPriority>
                value={story.priority}
                options={[
                  { value: 'high', label: <Chip size="sm" tone="danger">high</Chip> },
                  { value: 'med',  label: <Chip size="sm" tone="warning">med</Chip> },
                  { value: 'low',  label: <Chip size="sm" tone="neutral">low</Chip> },
                ]}
                display={<Chip size="sm" tone={priorityTone(story.priority)}>{story.priority}</Chip>}
                onCommit={v => patch({ priority: v as StoryPriority })}
              />
            </MetaRow>

            <MetaRow label="Planned start">
              <InlineDate
                value={story.planned_start ?? null}
                onCommit={v => patch({ planned_start: v })}
              />
            </MetaRow>

            <MetaRow label="Due">
              <InlineDate
                value={story.due_date}
                onCommit={v => patch({ due_date: v })}
              />
            </MetaRow>

            <MetaRow label="On calendar">
              <ScheduledSessions story={story} />
            </MetaRow>

            <MetaRow label="Actual start">
              <InlineDate
                value={story.actual_start ?? null}
                onCommit={v => patch({ actual_start: v })}
              />
            </MetaRow>

            <MetaRow label="Estimate">
              <InlineText
                value={story.estimate_points !== null ? String(story.estimate_points) : ''}
                onCommit={v => {
                  const num = v.trim() === '' ? null : Number(v)
                  if (num === null || (!Number.isNaN(num) && num >= 0)) {
                    patch({ estimate_points: num })
                  }
                }}
                placeholder="— pts"
                className="text-sm"
              />
            </MetaRow>

            <MetaRow label="Blocks">
              {story.blocks.length === 0 ? (
                <span className="text-sm text-fg-subtle">—</span>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {story.blocks.map(b => (
                    <Chip key={b.id} size="sm" tone="neutral" className="font-mono">
                      {b.key}
                    </Chip>
                  ))}
                </div>
              )}
            </MetaRow>

            <MetaRow label="Blocked by">
              {story.blocked_by.length === 0 ? (
                <span className="text-sm text-fg-subtle">—</span>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {story.blocked_by.map(b => (
                    <Chip
                      key={b.id}
                      size="sm"
                      tone={b.status_category === 'done' ? 'success' : 'danger'}
                      className="font-mono"
                    >
                      {b.key}
                    </Chip>
                  ))}
                </div>
              )}
            </MetaRow>

            <RecurrenceRow story={story} onPatch={patch} />

            <p className="text-[10px] italic text-fg-subtle pt-3">
              Click any field above to edit inline.
            </p>
          </aside>
        </div>
      )}
    </Drawer>
  )
}

// ─── Sections ────────────────────────────────────────────────────────────────

function Section({ title, children, right }: { title: string; children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-accent">{title}</h3>
        {right}
      </div>
      {children}
    </div>
  )
}

function RecurrenceRow({
  story,
  onPatch,
}: {
  story: StoryDetail
  onPatch: (p: Parameters<typeof updateStory>[1]) => void
}) {
  const isOn = !!story.is_recurring
  const rule = story.recurrence_rule
  const trigger = story.recurrence_trigger ?? 'on_completion'

  function describeRule(): string {
    if (!rule) return 'no rule set'
    const r = rule as { type: string; n?: number; days?: string[]; day_of_month?: number }
    if (r.type === 'daily') return 'daily'
    if (r.type === 'every_n_days') return `every ${r.n} days`
    if (r.type === 'weekly') return `weekly · ${(r.days ?? []).join(' ')}`
    if (r.type === 'every_n_weeks') return `every ${r.n} weeks · ${(r.days ?? []).join(' ')}`
    if (r.type === 'monthly') return `monthly on day ${r.day_of_month}`
    if (r.type === 'yearly') return 'yearly'
    return r.type
  }

  return (
    <div className="grid grid-cols-[90px_1fr] gap-2 items-start py-1.5 border-t border-border">
      <span className="text-[10px] uppercase tracking-wider text-fg-subtle font-medium pt-1">Recurring</span>
      <div className="space-y-1.5">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isOn}
            onChange={e => onPatch({
              is_recurring: e.target.checked ? 1 : 0,
              recurrence_rule: e.target.checked && !rule ? { type: 'daily' } : rule,
              recurrence_trigger: e.target.checked && !story.recurrence_trigger ? 'on_completion' : story.recurrence_trigger,
            })}
            className="accent-accent"
          />
          <span>Repeat</span>
        </label>
        {isOn && (
          <>
            <select
              value={rule?.type ?? 'daily'}
              onChange={e => {
                const t = e.target.value
                let next: typeof rule
                if (t === 'daily') next = { type: 'daily' }
                else if (t === 'every_n_days') next = { type: 'every_n_days', n: 3 }
                else if (t === 'weekly') next = { type: 'weekly', days: ['Mon'] }
                else if (t === 'every_n_weeks') next = { type: 'every_n_weeks', n: 2, days: ['Mon'] }
                else if (t === 'monthly') next = { type: 'monthly', day_of_month: 1 }
                else next = { type: 'yearly', month: 1, day_of_month: 1 }
                onPatch({ recurrence_rule: next })
              }}
              className="w-full text-xs rounded border border-border bg-bg px-1.5 py-1"
            >
              <option value="daily">Daily</option>
              <option value="every_n_days">Every N days</option>
              <option value="weekly">Weekly</option>
              <option value="every_n_weeks">Every N weeks</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
            <select
              value={trigger}
              onChange={e => onPatch({ recurrence_trigger: e.target.value as 'start_date' | 'on_completion' })}
              className="w-full text-xs rounded border border-border bg-bg px-1.5 py-1"
            >
              <option value="on_completion">On completion</option>
              <option value="start_date">By start date (cron)</option>
            </select>
            <p className="text-[10px] text-fg-subtle font-mono">{describeRule()}</p>
          </>
        )}
        {story.parent_story_id && (
          <p className="text-[10px] text-fg-subtle">
            instance of <span className="font-mono">#{story.parent_story_id}</span>
          </p>
        )}
      </div>
    </div>
  )
}

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[90px_1fr] gap-2 items-center py-1.5 border-b border-border last:border-b-0">
      <span className="text-[10px] uppercase tracking-wider text-fg-subtle font-medium">{label}</span>
      <span className="min-w-0">{children}</span>
    </div>
  )
}

/** The story's calendar work sessions, linking to the day of the next one. */
function ScheduledSessions({ story }: { story: StoryDetail }) {
  const sessions = story.sessions ?? []
  const nowDate = useNow(60_000)
  if (sessions.length === 0) {
    return <a href="/calendar" className="text-xs text-fg-subtle hover:text-accent">Not scheduled — add on calendar</a>
  }
  const now = nowDate.getTime()
  const next = sessions.find(ss => new Date(ss.end_at.replace(' ', 'T')).getTime() >= now) ?? sessions[sessions.length - 1]
  const minutes = sessions.reduce((t, ss) =>
    t + (new Date(ss.end_at.replace(' ', 'T')).getTime() - new Date(ss.start_at.replace(' ', 'T')).getTime()) / 60_000, 0)
  const nextDay = next.start_at.slice(0, 10)
  return (
    <a href={`/calendar?date=${nextDay}`} className="text-xs text-accent hover:underline" title="Open the calendar on the next session">
      {sessions.length} {sessions.length === 1 ? 'session' : 'sessions'} · {Math.round(minutes / 6) / 10}h
      {' · next '}{new Date(next.start_at.replace(' ', 'T')).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
    </a>
  )
}

function SubtasksSection({ story }: { story: StoryDetail }) {
  const qc = useQueryClient()
  const [draft, setDraft] = useState('')
  const subtasks = story.subtasks
  const done = subtasks.filter(s => s.done).length

  const addMut = useMutation({
    mutationFn: (title: string) => addSubtask(story.id, title),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['story', story.key] }),
  })
  const toggleMut = useMutation({
    mutationFn: (s: { id: number; done: boolean }) => updateSubtask(story.id, s.id, { done: s.done }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['story', story.key] }),
  })
  const delMut = useMutation({
    mutationFn: (id: number) => deleteSubtask(story.id, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['story', story.key] }),
  })
  const [pendingDelete, setPendingDelete] = useState<{ id: number; title: string } | null>(null)

  function onAdd(e: FormEvent) {
    e.preventDefault()
    if (!draft.trim()) return
    addMut.mutate(draft.trim())
    setDraft('')
  }

  return (
    <Section
      title={`Subtasks · ${done} of ${subtasks.length} done`}
    >
      <ul className="space-y-1">
        {subtasks.map(s => (
          <li
            key={s.id}
            className={cn(
              'flex items-center gap-3 px-2.5 py-1.5 rounded-md border border-border',
              s.done && 'bg-success/10',
            )}
          >
            <input
              type="checkbox"
              checked={s.done}
              onChange={e => toggleMut.mutate({ id: s.id, done: e.target.checked })}
              className="h-4 w-4 accent-accent"
            />
            <span className={cn('text-sm flex-1', s.done && 'line-through text-fg-subtle')}>
              {s.title}
            </span>
            <button
              onClick={() => setPendingDelete({ id: s.id, title: s.title })}
              className="text-fg-subtle hover:text-danger p-1 rounded hover:bg-danger/10"
              aria-label="Delete subtask"
            >
              <Trash2 size={12} />
            </button>
          </li>
        ))}
      </ul>
      <form onSubmit={onAdd} className="mt-2 flex gap-2">
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder="Add a subtask…"
          className="flex-1 rounded-md border border-border bg-bg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
        />
        <Button type="submit" size="sm" leftIcon={<Plus size={12} />} disabled={!draft.trim() || addMut.isPending}>
          Add
        </Button>
      </form>
      {delMut.error && (
        <p className="mt-1 text-xs text-danger">Couldn't delete subtask: {(delMut.error as Error).message}</p>
      )}
      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => { if (!o) setPendingDelete(null) }}
        title="Delete subtask?"
        description={pendingDelete ? `"${pendingDelete.title}" and its comments will be removed.` : undefined}
        onConfirm={() => { if (pendingDelete) delMut.mutate(pendingDelete.id) }}
      />
    </Section>
  )
}

function CommentsSection({ storyId }: { storyId: number }) {
  const qc = useQueryClient()
  const [draft, setDraft] = useState('')
  const commentsQ = useQuery({
    queryKey: ['comments', storyId],
    queryFn: () => listComments(storyId),
    refetchOnWindowFocus: true,
  })
  // Inline images in a comment body are `![](attachment:41)` references into
  // the story's own attachment list, so one query serves every comment.
  const attachmentsQ = useAttachments('story', storyId)
  const urls = new Map<number, string>(
    (attachmentsQ.data ?? [])
      .filter((a: Attachment) => a.content_url !== null)
      .map((a: Attachment) => [a.id, a.content_url as string]),
  )

  const addMut = useMutation({
    mutationFn: (body: string) => addComment(storyId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comments', storyId] }),
  })
  const delMut = useMutation({
    mutationFn: (id: number) => deleteComment(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comments', storyId] }),
  })

  /**
   * A comment has no id until it is posted, so an image pasted into the
   * composer attaches to the *story* — which is also what JIRA does, and means
   * it shows up in the Attachments panel as well as inline here.
   */
  async function uploadForComment(file: File): Promise<string | null> {
    const uploaded = await uploadAttachment('story', storyId, file)
    await qc.invalidateQueries({ queryKey: attachmentsKey('story', storyId) })
    const label = uploaded.file_name ?? 'attachment'
    return isImage(uploaded)
      ? `\n![${label}](attachment:${uploaded.id})\n`
      : `\n[${label}](attachment:${uploaded.id})\n`
  }

  function onAdd(e?: FormEvent) {
    e?.preventDefault()
    if (!draft.trim()) return
    addMut.mutate(draft.trim())
    setDraft('')
  }

  return (
    <Section title="Activity & comments">
      {commentsQ.isLoading && <p className="text-sm text-fg-subtle">Loading comments…</p>}
      <ul className="space-y-3">
        {(commentsQ.data ?? []).map(c => (
          <li key={c.id} className="flex gap-3 group">
            <Avatar size="sm" name={c.user_name ?? undefined} color={c.user_avatar_color ?? '#9aa0a6'} />
            <div className="flex-1 min-w-0 rounded-md border border-border bg-bg-elevated p-3">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs">
                  <span className="font-medium text-fg">{c.user_name}</span>
                  <span className="text-fg-subtle"> · {timeAgo(c.created_at)}</span>
                </div>
                <button
                  onClick={() => delMut.mutate(c.id)}
                  className="opacity-0 group-hover:opacity-100 text-fg-subtle hover:text-danger p-1 rounded"
                >
                  <Trash2 size={12} />
                </button>
              </div>
              <CommentBody body={c.body} format={c.body_format} urls={urls} />
            </div>
          </li>
        ))}
      </ul>
      <form onSubmit={onAdd} className="mt-3">
        <MarkdownComposer
          value={draft}
          onChange={setDraft}
          onSubmit={() => onAdd()}
          rows={3}
          placeholder="Add a comment… markdown welcome, ⌘+Enter to post"
          onUploadFile={uploadForComment}
          previewUrls={urls}
          disabled={addMut.isPending}
        />
        <div className="flex justify-end mt-2">
          <Button type="submit" size="sm" disabled={!draft.trim() || addMut.isPending}>
            {addMut.isPending ? 'Posting…' : 'Comment'}
          </Button>
        </div>
      </form>
    </Section>
  )
}

/**
 * Legacy comments are stored as 'plain' and keep the exact pre-markdown
 * rendering path; only bodies written by the markdown composer get parsed.
 */
function CommentBody({
  body,
  format,
  urls,
}: {
  body: string
  format: 'plain' | 'markdown'
  urls: Map<number, string>
}) {
  if (format !== 'markdown') {
    return <p className="text-sm text-fg whitespace-pre-wrap">{body}</p>
  }
  return (
    <div
      className="prose-comment text-sm text-fg"
      dangerouslySetInnerHTML={{ __html: renderMarkdown(body, urls) }}
    />
  )
}

function timeAgo(ts: string): string {
  const t = new Date(ts.replace(' ', 'T')).getTime()
  const diff = Date.now() - t
  const m = Math.floor(diff / 60_000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  return `${d}d ago`
}
